import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import React, { useEffect, useState } from 'react';
import { toast } from "sonner";

const TCEBPPresetModal = ({ isOpen, onClose, onSave, initialPreset }) => {
   const defaultSelectedDocuments = {
      letterOfIntent: true,
      applicationLetter: false,
      lguEndorsement: false,
      landTenurial: false,
      siteDevelopmentPlan: false,
      environmentalCompliance: false,
      fpic: false,
      ownerConsent: false,
      pambClearance: false
   };

   const [preset, setPreset] = useState({
      requestType: 'Cutting',
      name: '',
      projectLocation: '',
      officeName: '',
      officeAddress: '',
      contactNumber: '',
      purpose: '',
      isWithinTimberland: false,
      isTitledProperty: false,
      isWithinProtectedArea: false,
      isRequestForCutting: true,
      isRequestForEarthBalling: false,
      inventoryPurpose: '',
      selectedDocuments: defaultSelectedDocuments
   });

   useEffect(() => {
      if (initialPreset) {
         setPreset({
            ...initialPreset,
            selectedDocuments: {
               ...defaultSelectedDocuments,
               ...(initialPreset.selectedDocuments || {})
            }
         });
      }
   }, [initialPreset]);


   const handleRequestTypeChange = (value) => {
      console.log('Changing request type to:', value);
      setPreset(prev => ({
         ...prev,
         requestType: value,
         inventoryPurpose: value === 'Inventory' ? prev.inventoryPurpose : '',
         isRequestForCutting: value === 'Cutting',
         isRequestForEarthBalling: value === 'Earth Balling',
         selectedDocuments: value === 'Inventory' ? {
            letterOfIntent: true,
            applicationLetter: false,
            lguEndorsement: false,
            landTenurial: false,
            siteDevelopmentPlan: false,
            environmentalCompliance: false,
            fpic: false,
            ownerConsent: false,
            pambClearance: false
         } : defaultSelectedDocuments
      }));
   };

   const handleInventoryPurposeChange = (value) => {
      setPreset(prev => ({
         ...prev,
         inventoryPurpose: value,
         isRequestForCutting: value === 'forCutting',
         isRequestForEarthBalling: value === 'forEarthBalling'
      }));
   };

   const handleCheckboxChange = (name, checked) => {
      if (preset.requestType === 'Inventory') return;

      setPreset(prev => ({
         ...prev,
         [name]: checked,
         selectedDocuments: {
            ...prev.selectedDocuments,
            landTenurial: name === 'isTitledProperty' ? checked : prev.selectedDocuments.landTenurial,
            pambClearance: name === 'isWithinProtectedArea' ? checked : prev.selectedDocuments.pambClearance,
            fpic: name === 'isWithinTimberland' ? checked : prev.selectedDocuments.fpic
         }
      }));
   };

   const renderFileRequirements = () => {
      if (preset.requestType === 'Inventory') {
         return (
            <div key="letterOfIntent" className="flex items-center space-x-2 mb-4">
               <Checkbox
                  id="letterOfIntent"
                  checked={preset.selectedDocuments.letterOfIntent}
                  onCheckedChange={(checked) => {
                     setPreset(prev => ({
                        ...prev,
                        selectedDocuments: {
                           ...prev.selectedDocuments,
                           letterOfIntent: checked
                        }
                     }));
                  }}
               />
               <div>
                  <Label htmlFor="letterOfIntent" className="text-sm text-muted-foreground">Letter of Intent</Label>
                  <div className="mt-1 text-xs text-gray-500">Required</div>
               </div>
            </div>
         );
      }

      const fileTypes = [
         { type: 'applicationLetter', label: 'Application Letter', condition: true },
         { type: 'lguEndorsement', label: 'LGU Endorsement', condition: true },
         { type: 'landTenurial', label: 'Land Tenurial Instrument', condition: preset.isWithinTimberland },
         { type: 'siteDevelopmentPlan', label: 'Site Development Plan', condition: true },
         { type: 'environmentalCompliance', label: 'Environmental Compliance Certificate', condition: true },
         { type: 'fpic', label: 'FPIC/ICC Certificate', condition: preset.isWithinTimberland },
         { type: 'ownerConsent', label: 'Owner Consent', condition: preset.isTitledProperty },
         { type: 'pambClearance', label: 'PAMB Clearance', condition: preset.isWithinProtectedArea }
      ];

      return fileTypes
         .filter(({ condition }) => condition)
         .map(({ type, label }) => (
            <div key={type} className="flex items-center space-x-2 mb-4">
               <Checkbox
                  id={type}
                  checked={preset.selectedDocuments[type]}
                  onCheckedChange={(checked) => {
                     setPreset(prev => ({
                        ...prev,
                        selectedDocuments: {
                           ...prev.selectedDocuments,
                           [type]: checked
                        }
                     }));
                  }}
               />
               <div>
                  <Label htmlFor={type} className="text-sm text-muted-foreground">{label}</Label>
                  <div className="mt-1 text-xs text-gray-500">
                     {type === 'landTenurial' || type === 'fpic' || type === 'pambClearance' ? 'Conditional' : 'Required'}
                  </div>
               </div>
            </div>
         ));
   };

   const handleSave = () => {
      const presetToSave = {
         ...preset,
         requestType: preset.requestType,
         selectedDocuments: preset.selectedDocuments
      };
      console.log('Saving TCEBP preset:', presetToSave);
      localStorage.setItem('tcebpPreset', JSON.stringify(presetToSave));
      onSave(presetToSave);
      onClose();
      toast.success('TCEBP preset saved successfully');
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-[600px] max-h-[80vh]">
            <DialogHeader>
               <DialogTitle>Edit TCEBP Application Preset</DialogTitle>
               <DialogDescription>
                  Configure default values for test TCEBP applications
               </DialogDescription>
            </DialogHeader>

            <ScrollArea className="h-[500px] pr-4">
               <div className="space-y-4 pb-4">
                  <div>
                     <Label className="text-base">Request Type</Label>
                     <RadioGroup
                        value={preset.requestType}
                        onValueChange={handleRequestTypeChange}
                        className="mt-2 space-y-2"
                     >
                        <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
                           <RadioGroupItem value="Cutting" id="cutting" />
                           <Label htmlFor="cutting" className="flex-grow text-foreground">
                              Request for Cutting
                           </Label>
                        </div>
                        <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
                           <RadioGroupItem value="Earth Balling" id="earthBalling" />
                           <Label htmlFor="earthBalling" className="flex-grow text-foreground">
                              Request for Earth Balling
                           </Label>
                        </div>
                        <div className="flex flex-col space-y-2">
                           <div className="flex items-center space-x-2 p-4 rounded-lg border border-input bg-background hover:bg-accent">
                              <RadioGroupItem value="Inventory" id="inventory" />
                              <Label htmlFor="inventory" className="flex-grow text-foreground">
                                 Request for Inventory
                              </Label>
                           </div>
                           {preset.requestType === 'Inventory' && (
                              <div className="ml-6 space-y-2 p-4 border-l-2 border-input">
                                 <Label className="text-sm font-medium mb-2">What is this inventory request for?</Label>
                                 <RadioGroup
                                    value={preset.inventoryPurpose}
                                    onValueChange={handleInventoryPurposeChange}
                                    className="space-y-2"
                                 >
                                    <div className="flex items-center space-x-2">
                                       <RadioGroupItem value="forCutting" id="forCutting" />
                                       <Label htmlFor="forCutting">For Tree Cutting</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                       <RadioGroupItem value="forEarthBalling" id="forEarthBalling" />
                                       <Label htmlFor="forEarthBalling">For Earth Balling</Label>
                                    </div>
                                 </RadioGroup>
                              </div>
                           )}
                        </div>
                     </RadioGroup>
                  </div>

                  {preset.requestType !== 'Inventory' && (
                     <div className="space-y-2">
                        <Label className="text-base">Additional Information</Label>
                        <div className="space-y-2">
                           <div className="flex items-center space-x-2">
                              <Checkbox
                                 id="isWithinTimberland"
                                 checked={preset.isWithinTimberland}
                                 onCheckedChange={(checked) => handleCheckboxChange('isWithinTimberland', checked)}
                              />
                              <Label htmlFor="isWithinTimberland">Within Timberland</Label>
                           </div>
                           <div className="flex items-center space-x-2">
                              <Checkbox
                                 id="isTitledProperty"
                                 checked={preset.isTitledProperty}
                                 onCheckedChange={(checked) => handleCheckboxChange('isTitledProperty', checked)}
                              />
                              <Label htmlFor="isTitledProperty">Titled Property</Label>
                           </div>
                           <div className="flex items-center space-x-2">
                              <Checkbox
                                 id="isWithinProtectedArea"
                                 checked={preset.isWithinProtectedArea}
                                 onCheckedChange={(checked) => handleCheckboxChange('isWithinProtectedArea', checked)}
                              />
                              <Label htmlFor="isWithinProtectedArea">Within Protected Area</Label>
                           </div>
                        </div>
                     </div>
                  )}

                  <div className="border-t border-gray-200 pt-4 mt-4">
                     <h3 className="text-lg font-semibold mb-4">Required Documents</h3>
                     {renderFileRequirements()}
                  </div>
               </div>
            </ScrollArea>

            <DialogFooter>
               <Button variant="outline" onClick={onClose}>Cancel</Button>
               <Button onClick={handleSave}>Save Preset</Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export default TCEBPPresetModal;